'use client'

import React, { useState, useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import Link from 'next/link'
import { parseContentSegments } from '@/lib/mentions/tokens'
import { MentionPopover } from '@/components/mentions/MentionPopover'

// Module-level constants — stable references, never recreated
const remarkPlugins = [remarkGfm]

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }}
      className="absolute top-2 right-2 rounded border border-atrium-border bg-white/90 px-2 py-1 text-xs text-[#939294] opacity-0 transition-opacity hover:text-atrium-black group-hover:opacity-100"
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  )
}

function extractText(node: React.ReactNode): string {
  if (typeof node === 'string') return node
  if (Array.isArray(node)) return node.map(extractText).join('')
  if (node && typeof node === 'object' && 'props' in (node as React.ReactElement)) {
    return extractText((node as React.ReactElement).props.children)
  }
  return ''
}

/** Derive a human-readable label from a URL for the hover card. */
function getLinkLabel(href: string): string {
  try {
    const url = new URL(href, window.location.href)
    if (url.pathname && url.pathname !== '/') {
      // Internal or path-only: show the last meaningful path segment
      const parts = url.pathname.split('/').filter(Boolean)
      return parts.length > 0
        ? parts[parts.length - 1].replace(/[-_]/g, ' ')
        : url.hostname
    }
    return url.hostname
  } catch {
    return href
  }
}

/** Floating hover card that appears over a link with a domain label + Quote button. */
function LinkHoverCard({
  href,
  children,
  onQuote,
}: {
  href: string
  children: React.ReactNode
  onQuote?: (url: string, text: string) => void
}) {
  const [visible, setVisible] = useState(false)
  const isInternal = href.startsWith('/') || href.startsWith('#')

  const linkEl = isInternal ? (
    <Link
      href={href}
      className="text-accent-600 font-semibold hover:underline"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
    </Link>
  ) : (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-accent-600 underline hover:no-underline"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
    </a>
  )

  return (
    <span className="relative inline">
      {linkEl}
      {visible && onQuote && (
        <span
          className="absolute bottom-full left-0 mb-1 z-50 flex items-center gap-1.5 rounded-lg border border-atrium-border bg-atrium-inner shadow-md px-2 py-1 text-xs text-atrium-black whitespace-nowrap pointer-events-auto"
          onMouseEnter={() => setVisible(true)}
          onMouseLeave={() => setVisible(false)}
        >
          <span className="text-[#939294] truncate max-w-[160px]">
            {getLinkLabel(href)}
          </span>
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              const linkText = extractText(children)
              onQuote(href, linkText || href)
              setVisible(false)
            }}
            className="shrink-0 rounded px-1.5 py-0.5 bg-accent-600/10 text-accent-600 hover:bg-accent-600/20 transition-colors font-medium"
          >
            Quote
          </button>
        </span>
      )}
    </span>
  )
}

// Base components that don't depend on callbacks — stable reference
const baseMarkdownComponents = {
  pre({ children, ...props }: React.ComponentPropsWithoutRef<'pre'> & { children?: React.ReactNode }) {
    const codeText = extractText(children)
    return (
      <div className="relative group my-3">
        <pre className="overflow-x-auto rounded-lg bg-neutral-100 dark:bg-atrium-content border border-atrium-border p-4 text-sm text-atrium-black" {...props}>
          {children}
        </pre>
        <CopyButton text={codeText} />
      </div>
    )
  },
  code({ className, children, ...props }: React.ComponentPropsWithoutRef<'code'> & { children?: React.ReactNode }) {
    const isInline = !className
    if (isInline) {
      return (
        <code className="bg-neutral-100 dark:bg-atrium-content border border-atrium-border rounded px-1.5 py-0.5 text-sm text-atrium-black" {...props}>
          {children}
        </code>
      )
    }
    return <code className={className} {...props}>{children}</code>
  },
  p({ children }: { children?: React.ReactNode }) {
    return <p className="mb-3 last:mb-0 leading-relaxed">{children}</p>
  },
  ul({ children }: { children?: React.ReactNode }) {
    return <ul className="mb-3 ml-4 list-disc space-y-1">{children}</ul>
  },
  ol({ children }: { children?: React.ReactNode }) {
    return <ol className="mb-3 ml-4 list-decimal space-y-1">{children}</ol>
  },
  li({ children }: { children?: React.ReactNode }) {
    return <li className="leading-relaxed">{children}</li>
  },
  h1({ children }: { children?: React.ReactNode }) {
    return <h1 className="text-xl font-bold mt-4 mb-2 text-atrium-black">{children}</h1>
  },
  h2({ children }: { children?: React.ReactNode }) {
    return <h2 className="text-lg font-bold mt-4 mb-2 text-atrium-black">{children}</h2>
  },
  h3({ children }: { children?: React.ReactNode }) {
    return <h3 className="text-base font-bold mt-3 mb-1 text-atrium-black">{children}</h3>
  },
  blockquote({ children }: { children?: React.ReactNode }) {
    return <blockquote className="border-l-2 border-neutral-300 pl-4 italic text-atrium-muted my-3">{children}</blockquote>
  },
  table({ children }: { children?: React.ReactNode }) {
    return (
      <div className="overflow-x-auto my-3">
        <table className="min-w-full border border-atrium-border text-sm">{children}</table>
      </div>
    )
  },
  th({ children }: { children?: React.ReactNode }) {
    return <th className="border border-atrium-border bg-neutral-100 px-3 py-1.5 text-left font-medium">{children}</th>
  },
  td({ children }: { children?: React.ReactNode }) {
    return <td className="border border-atrium-border px-3 py-1.5">{children}</td>
  },
  hr() {
    return <hr className="my-4 border-atrium-border" />
  },
}

export const ChatMarkdown = React.memo(function ChatMarkdown({
  content,
  onLinkQuote,
}: {
  content: string
  onLinkQuote?: (url: string, text: string) => void
}) {
  const components = useMemo(() => ({
    ...baseMarkdownComponents,
    a({ href, children }: { href?: string; children?: React.ReactNode }) {
      if (!href) return <span>{children}</span>
      return (
        <LinkHoverCard href={href} onQuote={onLinkQuote}>
          {children}
        </LinkHoverCard>
      )
    },
  }), [onLinkQuote])

  const segments = useMemo(() => parseContentSegments(content), [content])
  const hasMentions = segments.some(s => s.type === 'mention')

  if (!hasMentions) {
    return (
      <ReactMarkdown remarkPlugins={remarkPlugins} components={components}>
        {content}
      </ReactMarkdown>
    )
  }

  // Pre-process: split on mention tokens and render each segment
  return (
    <>
      {segments.map((seg, i) => {
        if (seg.type === 'mention') {
          return (
            <MentionPopover
              key={i}
              kind={seg.kind}
              id={seg.id}
              display={seg.display}
            />
          )
        }
        if (!seg.value) return null
        return (
          <ReactMarkdown key={i} remarkPlugins={remarkPlugins} components={components}>
            {seg.value}
          </ReactMarkdown>
        )
      })}
    </>
  )
})
